import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Store, Package, Megaphone, BarChart3, Settings, CreditCard, 
  Plus, ArrowRight, TrendingUp, Eye, MousePointer, ShoppingBag,
  Clock, AlertTriangle, CheckCircle, Sparkles, ClipboardList, Sun, Leaf, Gift, Camera, Tag, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessDashboardPage() {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/business/register');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch business profile
      const profileRes = await fetch(`${API_URL}/api/business/profile`, { headers });
      if (!profileRes.ok) {
        if (profileRes.status === 404) {
          navigate('/business/register');
          return;
        }
        throw new Error('Erreur de chargement');
      }
      const profileData = await profileRes.json();
      setBusiness(profileData);

      // Fetch subscription
      const subRes = await fetch(`${API_URL}/api/business/subscription`, { headers });
      if (subRes.ok) {
        setSubscription(await subRes.json());
      }

      // Fetch campaigns
      const campRes = await fetch(`${API_URL}/api/business/campaigns`, { headers });
      if (campRes.ok) {
        setCampaigns(await campRes.json());
      }

      // Try to fetch analytics (may fail if not Pro/Premium)
      const analyticsRes = await fetch(`${API_URL}/api/business/analytics`, { headers });
      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }

      // Fetch order stats
      const orderStatsRes = await fetch(`${API_URL}/api/business/orders/stats`, { headers });
      if (orderStatsRes.ok) {
        setOrderStats(await orderStatsRes.json());
      }
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!business) return null;
    
    const status = business.subscription_status;
    if (status === 'trial') {
      const trialEnds = new Date(business.trial_ends_at);
      const daysLeft = Math.ceil((trialEnds - new Date()) / (1000 * 60 * 60 * 24));
      return (
        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm">
          <Clock className="w-3 h-3" />
          Essai gratuit - {daysLeft} jours restants
        </span>
      );
    }
    if (status === 'active') {
      return (
        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
          <CheckCircle className="w-3 h-3" />
          Abonnement actif
        </span>
      );
    }
    if (status === 'expired') {
      return (
        <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
          <AlertTriangle className="w-3 h-3" />
          Abonnement expiré
        </span>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Sub-Header */}
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {getStatusBadge()}
            </div>
            <Button variant="outline" size="sm" asChild className="text-xs whitespace-nowrap">
              <Link to={`/store/${business?.id}`}>Voir ma boutique</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
        {/* Welcome */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-lg sm:text-xl font-bold text-charcoal mb-1 truncate">
            Bonjour, {business?.business_name} 👋
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Gérez votre boutique, votre catalogue et vos campagnes publicitaires.
          </p>
        </div>

        {/* Quick Stats */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Package className="w-4 h-4" />
                <span className="text-sm">Produits</span>
              </div>
              <p className="text-2xl font-bold text-charcoal">{analytics.products_count}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Eye className="w-4 h-4" />
                <span className="text-sm">Vues campagnes</span>
              </div>
              <p className="text-2xl font-bold text-charcoal">{analytics.total_campaign_views}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <MousePointer className="w-4 h-4" />
                <span className="text-sm">Clics</span>
              </div>
              <p className="text-2xl font-bold text-charcoal">{analytics.total_campaign_clicks}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">Taux de clic</span>
              </div>
              <p className="text-2xl font-bold text-charcoal">{analytics.click_rate}%</p>
            </div>
          </div>
        )}

        {/* Order Stats Alert */}
        {orderStats && (orderStats.today.pending > 0 || orderStats.today.ready > 0) && (
          <Link to="/business/orders" className="block mb-6">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-charcoal">Commandes à traiter</h3>
                  <p className="text-sm text-muted-foreground">
                    {orderStats.today.pending > 0 && `${orderStats.today.pending} en attente`}
                    {orderStats.today.pending > 0 && orderStats.today.ready > 0 && ' • '}
                    {orderStats.today.ready > 0 && `${orderStats.today.ready} prête${orderStats.today.ready > 1 ? 's' : ''}`}
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-amber-600" />
            </div>
          </Link>
        )}

        {/* Main Navigation Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Orders - Click & Collect */}
          <Link to="/business/orders" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group border-2 border-amber-200">
            <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Commandes Click & Collect
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Gérez les commandes et les créneaux de retrait
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              {orderStats ? `${orderStats.today.orders_count} aujourd'hui` : 'Gérer'} <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Profile */}
          <Link to="/business/profile" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
              <Store className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Mon profil boutique
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Photos, description, horaires, marques et producteurs locaux
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              Gérer <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Catalog */}
          <Link to="/business/catalog" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
              <Package className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Mon catalogue
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Gérez vos produits, prix et promotions en cours
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              {analytics ? `${analytics.products_count} produits` : 'Gérer'} <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Campaigns */}
          <Link to="/business/campaigns" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group">
            <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4">
              <Megaphone className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Mes campagnes
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Créez des bannières et offres promotionnelles ciblées
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              {campaigns.length} campagne{campaigns.length > 1 ? 's' : ''} <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Analytics */}
          <Link 
            to="/business/analytics" 
            className={`bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group ${
              subscription?.plan_details?.features?.analytics_basic ? '' : 'opacity-60'
            }`}
          >
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Statistiques
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Analysez vos performances et votre positionnement prix
            </p>
            {subscription?.plan_details?.features?.analytics_basic ? (
              <span className="text-primary text-sm font-medium flex items-center gap-1">
                Consulter <ArrowRight className="w-4 h-4" />
              </span>
            ) : (
              <span className="text-amber-600 text-sm font-medium flex items-center gap-1">
                <Sparkles className="w-4 h-4" /> Forfait Pro requis
              </span>
            )}
          </Link>

          {/* Subscription */}
          <Link to="/business/subscription" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Mon abonnement
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Forfait {subscription?.plan_details?.name || 'Starter'} - Gérer mon abonnement
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              Gérer <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Settings */}
          <Link to="/business/settings" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group">
            <div className="w-12 h-12 bg-gray-500 rounded-xl flex items-center justify-center mb-4">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Paramètres
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Notifications, équipe, et paramètres de compte
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              Configurer <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Restaurant Menu (only for restaurants) */}
          {business?.business_type === 'restaurant' && (
            <Link to="/business/menu" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group" data-testid="restaurant-menu-link">
              <div className="w-12 h-12 bg-gradient-to-r from-rose-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                <ClipboardList className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
                Ma carte / Menu
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Gérez vos plats, menus et options à emporter
              </p>
              <span className="text-primary text-sm font-medium flex items-center gap-1">
                Gérer <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          )}

          {/* Restaurant Reviews (only for restaurants) */}
          {business?.business_type === 'restaurant' && (
            <Link to={`/business/${business.id}/reviews`} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group" data-testid="restaurant-reviews-link">
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">⭐</span>
              </div>
              <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
                Avis clients
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Consultez les avis de vos clients
              </p>
              <span className="text-primary text-sm font-medium flex items-center gap-1">
                Voir <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          )}

          {/* Daily Special (restaurants) */}
          {business?.business_type === 'restaurant' && (
            <Link to="/business/daily-special" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group" data-testid="daily-special-link">
              <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                <Sun className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
                Plat du jour
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Publiez votre plat du jour (gratuit, 1/jour)
              </p>
              <span className="text-primary text-sm font-medium flex items-center gap-1">
                Publier <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          )}

          {/* Dish Photos Management (restaurants only) */}
          {business?.business_type === 'restaurant' && (
            <Link to="/business/dish-photos" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group" data-testid="dish-photos-link">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
                Photos clients
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Approuvez les photos de plats et enrichissez votre galerie
              </p>
              <span className="text-primary text-sm font-medium flex items-center gap-1">
                Gérer <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          )}

          {/* Anti-Waste Boxes (all business types) */}
          <Link to="/business/anti-waste" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group" data-testid="anti-waste-link">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Colis Anti-Gaspi
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Réduisez le gaspillage et obtenez votre licence offerte
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              Gérer <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Coupons */}
          <Link to="/business/coupons" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group" data-testid="coupons-link">
            <div className="w-12 h-12 bg-gradient-to-r from-rose-500 to-red-500 rounded-xl flex items-center justify-center mb-4">
              <Tag className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Coupons promotionnels
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Vendez des coupons en ligne et attirez de nouveaux clients
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              Gérer <ArrowRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Boost */}
          <Link to="/business/boost" className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group" data-testid="boost-link">
            <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-charcoal mb-1 group-hover:text-primary transition-colors">
              Mise en avant
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Boostez votre visibilité dans l'annuaire et sur la carte
            </p>
            <span className="text-primary text-sm font-medium flex items-center gap-1">
              1€/jour/km <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>

        {/* Recent Campaigns */}
        {campaigns.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-charcoal">Campagnes récentes</h2>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/business/campaigns">Voir tout</Link>
              </Button>
            </div>
            <div className="space-y-3">
              {campaigns.slice(0, 3).map((campaign) => (
                <div key={campaign.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-charcoal">{campaign.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {campaign.views_count} vues • {campaign.clicks_count} clics
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    campaign.status === 'active' ? 'bg-green-100 text-green-700' :
                    campaign.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                    campaign.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {campaign.status === 'active' ? 'Active' :
                     campaign.status === 'draft' ? 'Brouillon' :
                     campaign.status === 'paused' ? 'En pause' : 'Terminée'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state for new users */}
        {!analytics && campaigns.length === 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-charcoal mb-2">Commencez par configurer votre boutique</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Ajoutez des photos, décrivez votre activité et commencez à ajouter vos produits au catalogue.
            </p>
            <div className="flex justify-center gap-4">
              <Button asChild>
                <Link to="/business/profile">
                  <Store className="w-4 h-4 mr-2" />
                  Configurer ma boutique
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/business/catalog">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter des produits
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}